import { inlineSvg, packUrl } from './svg.js';

/**
 * The menu vocabulary, drawn.
 *
 * Every goal and prize in the game was already an authored SVG in the content pack; the menus were the
 * one family still using system emoji, which had three costs:
 *
 *  - **They are a different drawing on every platform.** For a pre-reader an icon *is* the word, and
 *    iPadOS and Android do not draw the same house, crayon or biscuit. The set she learns on one tablet
 *    is not quite the set on the other.
 *  - **Two of them collided in meaning.** The Garage was a house and free play was a park - both "a
 *    place" - while the house also means *home* on the arrival beat and the Lookout is the actual home.
 *    A house that leads to the garage is the game telling her something untrue.
 *  - **They belonged to no family.** Emoji arrive with their own outlines, palettes and optical sizes,
 *    next to pack art that has one ink outline weight and five colours.
 *
 * The drawn set is in the pack's own idiom - see docs/ART_PIPELINE.md: a 256 box, flat fills, then one
 * `#1f1f1f` outline group at stroke-width 12 (9 for interior detail). The `menu icons` block in
 * `tests/content-icons.test.ts` holds new ones to it.
 *
 * Semantics, so the set says one thing each:
 *
 *  - a **place with a roof** is somewhere she goes: the Lookout (red pointed roof, yellow lamp) and the
 *    Garage (flat roof, open bay, a car parked in it). They cannot be confused for each other.
 *  - **free play is not a building at all** - hills and a sun - because it is not a room, it is *out*.
 *  - **prizes are the jar**, the same jar that fills up on the reward screen, rather than a biscuit:
 *    a biscuit is a thing she can be sent to fetch, and a prize is not.
 *  - an **object** is an action: the crayon colours, the bin throws away, the horn makes a noise.
 */

/** The drawn set. Every id is a file at `ui/<id>.svg` in the pack. */
export const MENU_ICONS = [
  'back',
  /**
   * The same paw, filled with `currentColor`, for decoration: the celebration burst, the ribbon
   * marker, the direction trail. `back` is the button and carries its own colour; this one is tinted
   * by whatever is drawing it - which is how the burst finally gets the pups' six colours it always
   * claimed to have. A colour emoji ignores CSS `color`, so every paw in that burst was the same
   * shade whatever the code set.
   */
  'paw',
  'arrow',
  'confirm',
  'start',
  'undo',
  'crayon',
  'car',
  'garage',
  'prizes',
  'freeplay',
  'bin',
  'signpost',
  'horn',
] as const;

export type MenuIconId = (typeof MENU_ICONS)[number];

function menuIconUrl(id: MenuIconId): string {
  return packUrl('ui', id);
}

/**
 * An inline `<svg>`, or null.
 *
 * Null is an expected outcome, not an error to handle at the call site: `applyMenuIcon` leaves the glyph
 * in place, so a missing file costs the picture and never the button.
 */
export function menuIconElement(id: MenuIconId): Promise<SVGSVGElement | null> {
  return inlineSvg(menuIconUrl(id));
}
